'use strict'

/* -----------------------------------------------------------------------------
 * dependencies
 * -------------------------------------------------------------------------- */

// 3rd party
import React, { useEffect, useState } from 'react'
import Icon from '@mdi/react'
import { mdiArrowLeft } from '@mdi/js'

// walrus
import Box from '@components/kit/box'
import Button from '@components/kit/button'
import { BoxLink } from '@components/kit/link'
import Loading from '@components/kit/loading'
import Modal from '@components/kit/modal'
import Text from '@components/kit/text'

// lib
import AppShell from '@components/ui/app-shell'
import BadgePill from '@components/ui/badge-pill'
import PageHeader from '@components/ui/page-header'
import { useAuthStore } from '@store/Auth'
import { AuthDevice, AuthDeviceStatus } from '@store/Auth/types'
import { colors } from '@theme'
import { formatRelativeTimeCondensed } from '@utils/date'
import DeviceIcon from './device-icon'

/* -----------------------------------------------------------------------------
 * helpers
 * -------------------------------------------------------------------------- */

const statusBadges: Record<
  AuthDeviceStatus,
  React.ComponentProps<typeof BadgePill>
> = {
  active: {
    label: 'Active',
    bgColor: 'proTealTint',
    dotColor: 'proTeal',
    labelColor: 'proTealDeep'
  },
  revoked: {
    label: 'Signed out',
    bgColor: 'proDangerTint',
    dotColor: 'proDanger',
    labelColor: 'proDangerDeep'
  },
  expired: {
    label: 'Expired',
    bgColor: 'proNeutralTint',
    dotColor: 'proTextFaint',
    labelColor: 'proTextMuted'
  }
}

const platformLabels: Record<AuthDevice['device']['platform'], string> = {
  web: 'Web',
  ios: 'iOS app',
  android: 'Android app',
  other: 'Other'
}

const ago = (value?: string | null) => {
  const relative = formatRelativeTimeCondensed(value ?? undefined)
  if (!relative) {
    return ''
  }
  return relative === 'now' ? 'just now' : `${relative} ago`
}

const describeActivity = (device: AuthDevice) => {
  if (device.status === 'revoked' && device.revoked.at) {
    return `Signed out ${ago(device.revoked.at)}`
  }
  return `Last active ${ago(device.last_activity_at)}`
}

/* -----------------------------------------------------------------------------
 * AuthDeviceRow
 * -------------------------------------------------------------------------- */

type AuthDeviceRowProps = {
  device: AuthDevice
  last: boolean
}

const AuthDeviceRow = ({ device, last }: AuthDeviceRowProps) => (
  <Box
    alignItems='center'
    borderColor='proDivider'
    borderBottomStyle='solid'
    borderBottomWidth={last ? 0 : 1}
    flexDirection='row'
    gap='16'
    px='20'
    py='16'
  >
    <DeviceIcon device={device.device} muted={device.status !== 'active'} />
    <Box flex='1 1 auto' minWidth={0}>
      <Box alignItems='center' flexDirection='row' gap='08'>
        <Text font='pro.text.14.semibold' textColor='proInk'>
          {device.device.name}
        </Text>
        {device.is_current ? (
          <BadgePill
            bgColor='proAccentTint'
            label='This device'
            labelColor='proAccentDeep'
            py='02'
          />
        ) : null}
      </Box>
      <Text font='pro.text.12.regular' mt='04' textColor='proTextMuted'>
        {`${
          platformLabels[device.device.platform] ?? platformLabels.other
        } · ${describeActivity(device)}`}
      </Text>
    </Box>
    <BadgePill alignSelf='center' {...statusBadges[device.status]} />
  </Box>
)

/* -----------------------------------------------------------------------------
 * AuthDevices
 * -------------------------------------------------------------------------- */

function AuthDevices() {
  const [devices, setDevices] = useState<AuthDevice[] | null>(null)
  const [error, setError] = useState('')
  const [isConfirmOpen, setIsConfirmOpen] = useState(false)
  const [isRevoking, setIsRevoking] = useState(false)

  useEffect(() => {
    useAuthStore
      .getState()
      .fetchAuthDevices()
      .then(result => {
        if (Array.isArray(result)) {
          setDevices(result)
        } else {
          setError('Could not load your devices.')
        }
      })
  }, [])

  // a successful revoke ends this session too, and the app returns to login
  const revokeAll = async () => {
    setIsRevoking(true)
    const result = await useAuthStore.getState().revokeAllAuthDevices()
    if (!('success' in result)) {
      setError('Could not sign out of all devices. Please try again.')
      setIsConfirmOpen(false)
      setIsRevoking(false)
    }
  }

  return (
    <AppShell activePage='settings' label='Devices'>
      <Box maxWidth={720} width='100%'>
        <BoxLink path='/settings' alignSelf='flex-start' mb='12'>
          <Box alignItems='center' flexDirection='row' gap='04'>
            <Icon path={mdiArrowLeft} size='16px' color={colors.proAccent} />
            <Text font='pro.text.13.medium' textColor='proAccent'>
              Settings
            </Text>
          </Box>
        </BoxLink>

        <PageHeader
          title='Devices'
          actions={
            <Button variant='tertiary' onPress={() => setIsConfirmOpen(true)}>
              Sign out of all devices
            </Button>
          }
        />

        <Text font='pro.text.13.regular' mb='16' textColor='proTextBody'>
          You can stay logged in on up to two devices. Logging in on a third
          signs out the one used least recently.
        </Text>

        {error ? (
          <Text font='pro.text.13.medium' mb='16' textColor='proDangerDeep'>
            {error}
          </Text>
        ) : null}

        <Box
          bg='proSurface'
          borderColor='proBorder'
          borderRadius='proXl'
          borderStyle='solid'
          borderWidth={1}
          boxShadow='proTable'
          overflowX='hidden'
          overflowY='hidden'
        >
          {devices === null && !error ? <Loading my='24' /> : null}

          {(devices ?? []).map((device, index) => (
            <AuthDeviceRow
              key={device._id}
              device={device}
              last={index === devices.length - 1}
            />
          ))}
        </Box>
      </Box>

      <Modal
        open={isConfirmOpen}
        onRequestClose={() => setIsConfirmOpen(false)}
        showCloseButton={false}
        maxWidth={440}
      >
        <Text font='pro.text.16.semibold' textColor='proInk'>
          Sign out of all devices?
        </Text>
        <Text font='pro.text.13.regular' mt='08' textColor='proTextBody'>
          Every device, this one included, will need to log in again.
        </Text>
        {isRevoking ? <Loading my='16' /> : null}
        <Box flexDirection='row' gap='12' justifyContent='flex-end' mt='24'>
          <Button
            variant='tertiary'
            disabled={isRevoking}
            onPress={() => setIsConfirmOpen(false)}
          >
            Cancel
          </Button>
          <Button variant='secondary' disabled={isRevoking} onPress={revokeAll}>
            Sign out
          </Button>
        </Box>
      </Modal>
    </AppShell>
  )
}

export default AuthDevices
